import React from 'react';
import { Form, Select, InputNumber, Checkbox } from 'antd';
import { FormInstance } from 'antd/es/form';
import FormSection from '../FormSection';
import CurvDown from '../svg/CurvDown';

const { Option } = Select;

interface LossesProps {
  form: FormInstance;
  caseType?: string;
  discriminationBasis?: string;
}

const Losses: React.FC<LossesProps> = ({ form, caseType, discriminationBasis }) => {
  const timeLostOptions = [
    'Seniority',
    'Sick time',
    'Time and a half',
    'Double time',
    'Vacation time',
    'Compensation',
    'Leave',
    'Overtime',
    'Personal time'
  ];

  const lostAdvancementOptions = [
    'Higher position',
    'Supervisor position'
  ];

  const lossOptions = [
    'Were unable to work overtime',
    'Lost advancement to a higher position',
    'Lost raises or bonuses',
    'Had no losses',
    'Had to take more sick time, personal time, comp time, vacation time, or time without pay'
  ];

  const shLossesName = ['sexual_harassment_info', 'harassment_losses'];
  const regularLossesName = ['losses_info', 'losses_selected'];

  const isSH = caseType === 'Sexual Harassment' && discriminationBasis === 'Sex';

  return (
    <div className="flex flex-col w-full h-full">
      <FormSection title="Losses" columns={1}>
        <div className="flex flex-col gap-0 w-full">
          {/* Instruction & Checkboxes - Only for Sexual Harassment */}
          {isSH && (
            <>
              <h3 className="font-['Lexend',sans-serif] text-[14px] md:text-[18px] font-medium text-[#1F2A37] mb-[16px]">
                You alege that as a further result of this conduct you (select all that apply)
              </h3>

              <div className="border border-[#1F2A3738] rounded-[8px] p-[17px] md:py-[28px] md:px-[31px] mb-10 w-full">
                <Form.Item
                  name={isSH ? shLossesName : regularLossesName}
                  className="mb-0 custom-checkbox-group"
                  getValueProps={(value) => {
                    if (Array.isArray(value)) {
                      return {
                        value: value.map((item: any) => {
                          if (typeof item === 'object' && item !== null && 'loss_type' in item) {
                            return item.loss_type;
                          }
                          return item;
                        }),
                      };
                    }
                    return { value: [] };
                  }}
                >
                  <Checkbox.Group className="w-full">
                    <style>
                      {`
                        .custom-checkbox-group .ant-checkbox-inner {
                          width: 22px !important;
                          height: 22px !important;
                          background-color: #555D6740 !important;
                          border-radius: 5px !important;
                          border: none !important;
                          box-shadow: none !important;
                        }
                        @media (max-width: 768px) {
                          .custom-checkbox-group .ant-checkbox-inner {
                            width: 16px !important;
                            height: 16px !important;
                          }
                        }
                        .custom-checkbox-group .ant-checkbox-checked .ant-checkbox-inner {
                          background-color: #555D6740 !important;
                          border: none !important;
                        }
                        .custom-checkbox-group .ant-checkbox-checked .ant-checkbox-inner::after {
                          width: 7px !important;
                          height: 10px !important;
                          border-color: #1F2A37 !important;
                          transform: rotate(45deg) scale(1.1) translate(-50%, -50%);
                          left: 25%;
                          top: 45%;
                        }
                        @media (max-width: 768px) {
                          .custom-checkbox-group .ant-checkbox-checked .ant-checkbox-inner::after {
                            width: 5px !important;
                            height: 8px !important;
                          }
                        }
                        .custom-checkbox-group .ant-checkbox-wrapper {
                           display: flex;
                           align-items: center;
                        }
                        .custom-checkbox-group .ant-checkbox {
                           top: 0;
                        }
                        .custom-checkbox-group .ant-checkbox-wrapper:hover .ant-checkbox-inner,
                        .custom-checkbox-group .ant-checkbox:hover .ant-checkbox-inner,
                        .custom-checkbox-group .ant-checkbox-input:focus + .ant-checkbox-inner {
                          border: none !important;
                          box-shadow: none !important;
                        }
                      `}
                    </style>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] md:gap-x-[14px] md:gap-y-[22px]">
                      {lossOptions.map((loss, index) => (
                        <div key={index} className="flex items-start">
                          <Checkbox
                            value={loss}
                            className="font-['Lexend',sans-serif] text-[14px] md:text-[14px] text-[#555D67] leading-[21px] font-light"
                          >
                            {loss}
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </Checkbox.Group>
                </Form.Item>
              </div>
            </>
          )}

          {/* Other Losses - Only for Non-Sexual Harassment */}
          {!isSH && (
            <>
              <div className="mb-3 md:mb-4">
                <h3 className="font-['Lexend',sans-serif] text-[14px] lg:text-[18px] font-medium text-[#1F2A37] leading-[29px] m-0">
                  Select any other losses you had:
                </h3>
              </div>

              {/* Bottom Grid for Other Losses */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-[60px] gap-y-[28px] w-full">
                {/* Field 1 */}
                <div className="flex flex-col">
                  <div className="flex items-end min-h-[38px]">
                    <label className="font-['Lexend',sans-serif] text-[12px] md:text-[16px] text-[#1F2A37] font-medium mb-3 md:mb-4">
                      Time Lost
                    </label>
                  </div>
                  <Form.Item name={['losses_info', 'time_lost']} className="mb-0">
                    <Select placeholder="Select" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}>
                      {timeLostOptions.map(option => (
                        <Option key={option} value={option}>{option}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                {/* Field 2 */}
                <div className="flex flex-col">
                  <div className="flex items-end min-h-[38px]">
                    <label className="font-['Lexend',sans-serif] text-[12px] md:text-[16px] text-[#1F2A37] font-medium mb-3 md:mb-4">
                      Cash amount lost
                    </label>
                  </div>
                  <Form.Item name={['losses_info', 'cash_amount_lost']} className="mb-0">
                    <InputNumber
                      placeholder="Enter Amount"
                      min={0}
                      precision={2}
                      className="w-full"
                      onKeyDown={(e) => {
                        const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                        if (allowed.includes(e.key)) return;
                        if (/^\d$/.test(e.key)) return;
                        if (e.key === '.' && !(e.currentTarget as HTMLInputElement).value.includes('.')) return;
                        e.preventDefault();
                      }}
                    />
                  </Form.Item>
                </div>

                {/* Field 3 */}
                <div className="flex flex-col">
                  <div className="flex items-end min-h-[38px]">
                    <label className="font-['Lexend',sans-serif] text-[12px] md:text-[16px] text-[#1F2A37] font-medium mb-3 md:mb-4">
                      Lost an advancement to
                    </label>
                  </div>
                  <Form.Item name={['losses_info', 'lost_advancement_to']} className="mb-0">
                    <Select placeholder="Select" suffixIcon={<CurvDown className="pt-[10px] md:pt-[14px]" />}>
                      {lostAdvancementOptions.map(option => (
                        <Option key={option} value={option}>{option}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              </div>
            </>
          )}
        </div>
      </FormSection>
    </div>
  );
};

export default Losses;
